import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDarkMode } from '../../context/DarkModeContext'

// The Crisp chat bubble sits above everything and covers the sheet's buttons on
// phones. One popup can close just before the next opens (preview → edit), and
// Crisp ignores a hide sent right after a show, so wait a tick before showing.
let openPopups = 0
const hideChatBubble = () => {
	openPopups += 1
	window.$crisp?.push(['do', 'chat:hide'])
}
const showChatBubbleWhenNoPopupIsOpen = () => {
	openPopups -= 1
	setTimeout(() => {
		if (openPopups === 0) window.$crisp?.push(['do', 'chat:show'])
	})
}

// Popup for the Email & Templates page: centered on desktop, a full-screen
// sheet on phones with the actions pinned to the bottom. Rendered into
// document.body because the phone settings view clips fixed-position children.
const EmailSettingsModal = ({ title, subtitle, onClose, footer, children, wide = false }) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode

	useEffect(() => {
		const closeOnEscape = event => {
			if (event.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', closeOnEscape)
		return () => document.removeEventListener('keydown', closeOnEscape)
	}, [onClose])

	useEffect(() => {
		hideChatBubble()
		return showChatBubbleWhenNoPopupIsOpen
	}, [])

	const divider = isLight ? 'border-gray-200' : 'border-white/10'

	return createPortal(
		<div className="relative z-[100]" role="dialog" aria-modal="true" aria-label={title}>
			<div className="fixed inset-0 z-[90] bg-black opacity-50" onClick={onClose} />
			<div className="fixed inset-0 z-[100] flex sm:items-start sm:justify-center sm:pt-[5.5rem] sm:px-4 pointer-events-none">
				<div
					className={`pointer-events-auto flex flex-col w-full h-full sm:h-auto sm:max-h-[85vh] ${wide ? 'sm:max-w-3xl' : 'sm:max-w-2xl'} sm:rounded-lg text-left ${
						isLight
							? 'bg-white sm:shadow-lg sm:ring-1 sm:ring-gray-200'
							: 'bg-[#15202b] sm:shadow-[0_0_15px_rgba(59,130,246,0.1)] sm:ring-1 sm:ring-white/10'
					}`}
				>
					<div className={`flex items-center justify-between gap-4 px-5 py-4 sm:px-6 border-b ${divider}`}>
						<div className="min-w-0">
							<h2 className={`text-base sm:text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>{title}</h2>
							{subtitle && <p className={`text-sm truncate ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>}
						</div>
						<button
							type="button"
							onClick={onClose}
							aria-label="Close"
							className={isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
					<div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
					<div className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-5 py-4 sm:px-6 border-t ${divider}`}>
						{footer}
					</div>
				</div>
			</div>
		</div>,
		document.body,
	)
}

export default EmailSettingsModal
